// File: src/utils/toast.js
// Purpose: Lightweight, dependency-free global toast system.
//
// Provides an imperative API (`toast.success(...)`, `toast.error(...)`, etc.)
// that works from anywhere — components, hooks, and plain utility modules —
// because it dispatches through a module-level singleton store rather than
// React context. A single <ToastContainer /> (see components/molecules)
// subscribes to this store and renders the toasts via a portal.

let toasts = [];
const listeners = new Set();
let idCounter = 0;

const DEFAULT_DURATION = 4000;
const ERROR_DURATION = 6000;

function emit() {
  for (const listener of listeners) listener(toasts);
}

/**
 * Subscribe to toast changes. Returns an unsubscribe function.
 * The listener is invoked immediately with the current toasts.
 * @param {(toasts: Array) => void} listener
 */
export function subscribe(listener) {
  listeners.add(listener);
  listener(toasts);
  return () => listeners.delete(listener);
}

/**
 * Add a toast. Returns its id so callers can dismiss it early.
 * @param {string} message
 * @param {{ type?: 'success'|'error'|'info'|'warning', duration?: number }} [options]
 */
export function addToast(message, options = {}) {
  const { type = 'info', duration } = options;
  const id = ++idCounter;
  const resolvedDuration =
    duration != null ? duration : type === 'error' ? ERROR_DURATION : DEFAULT_DURATION;

  toasts = [...toasts, { id, message, type }];
  emit();

  if (resolvedDuration > 0) {
    setTimeout(() => dismissToast(id), resolvedDuration);
  }
  return id;
}

/** Remove a toast by id. */
export function dismissToast(id) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Remove all toasts. */
export function clearToasts() {
  toasts = [];
  emit();
}

export const toast = {
  success: (message, options) => addToast(message, { ...options, type: 'success' }),
  error: (message, options) => addToast(message, { ...options, type: 'error' }),
  info: (message, options) => addToast(message, { ...options, type: 'info' }),
  warning: (message, options) => addToast(message, { ...options, type: 'warning' }),
};

// Expose the API on window in non-production builds for manual debugging and
// end-to-end (Playwright) tests. Never exposed in production bundles.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.toast = toast;
}

export default toast;
